import { useState, useEffect } from "react";
import { Eye, EyeOff, Lock, CheckCircle, XCircle } from "lucide-react";
import { toast } from "react-hot-toast";
import { getApiBaseUrl } from "../apis/getApiBaseUrl";

const API_BASE_URL = getApiBaseUrl();

export default function ResetPasswordPage() {
    const [newPassword, setNewPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [loading, setLoading] = useState(false);
    const [verifying, setVerifying] = useState(true);
    const [tokenValid, setTokenValid] = useState(false);
    const [userEmail, setUserEmail] = useState("");
    const [success, setSuccess] = useState(false);
    const [token, setToken] = useState("");

    // Password strength validation
    const [passwordStrength, setPasswordStrength] = useState({
        length: false,
        uppercase: false,
        lowercase: false,
        number: false,
        special: false
    });

    // Simulate getting token from URL params (in real app, use useParams from react-router-dom)
    useEffect(() => {
        // In a real app, you'd get this from useParams()
        const urlToken = window.location.pathname.split('/').pop();
        setToken(urlToken || 'demo-token-123');
    }, []);

    // Check password strength
    useEffect(() => {
        if (newPassword) {
            setPasswordStrength({
                length: newPassword.length >= 8,
                uppercase: /[A-Z]/.test(newPassword),
                lowercase: /[a-z]/.test(newPassword),
                number: /\d/.test(newPassword),
                special: /[!@#$%^&*(),.?":{}|<>]/.test(newPassword)
            });
        }
    }, [newPassword]);

    // Verify token on component mount
    useEffect(() => {
        const verifyToken = async () => {
            if (!token) {
                toast.error("Invalid reset link", {
                    duration: 4000,
                    position: 'top-center',
                });
                return;
            }

            try {
                const response = await fetch(`${API_BASE_URL}/password-reset/verify/${token}`);
                const data = await response.json();

                if (response.ok) {
                    setTokenValid(true);
                    setUserEmail(data.data?.emailid || "user@example.com");
                    toast.success("Reset link verified successfully!", {
                        duration: 3000,
                        position: 'top-center',
                    });
                } else {
                    toast.error(data.message || "Invalid or expired reset link", {
                        duration: 4000,
                        position: 'top-center',
                    });
                }
            } catch (error) {
                toast.error("Network error. Please check your connection.", {
                    duration: 4000,
                    position: 'top-center',
                });
            } finally {
                setVerifying(false);
            }
        };

        if (token) {
            verifyToken();
        }
    }, [token]);

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!newPassword || !confirmPassword) {
            toast.error("Please fill in all fields", {
                duration: 3000,
                position: 'top-center',
            });
            return;
        }

        if (newPassword !== confirmPassword) {
            toast.error("Passwords do not match", {
                duration: 3000,
                position: 'top-center',
            });
            return;
        }

        if (newPassword.length < 8) {
            toast.error("Password must be at least 8 characters long", {
                duration: 3000,
                position: 'top-center',
            });
            return;
        }

        setLoading(true);

        try {
            const response = await fetch(`${API_BASE_URL}/password-reset/reset/${token}`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    newPassword,
                    confirmPassword
                }),
            });

            const data = await response.json();

            if (response.ok) {
                setSuccess(true);
                toast.success("Password reset successful! Redirecting to login...", {
                    duration: 4000,
                    position: 'top-center',
                    icon: '🎉',
                });
                setTimeout(() => {
                    window.location.href = "/login";
                }, 3000);
            } else {
                toast.error(data.message || "Failed to reset password", {
                    duration: 4000,
                    position: 'top-center',
                });
            }
        } catch (error) {
            toast.error("Network error. Please try again.", {
                duration: 4000,
                position: 'top-center',
            });
        } finally {
            setLoading(false);
        }
    };

    const getPasswordStrengthColor = () => {
        const validCount = Object.values(passwordStrength).filter(Boolean).length;
        if (validCount < 2) return "text-red-400";
        if (validCount < 4) return "text-yellow-400";
        return "text-green-400";
    };

    const getPasswordStrengthText = () => {
        const validCount = Object.values(passwordStrength).filter(Boolean).length;
        if (validCount < 2) return "Weak";
        if (validCount < 4) return "Medium";
        return "Strong";
    };

    const backgroundStyle = {
        backgroundImage: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        backgroundSize: 'cover',
        backgroundPosition: 'center'
    };

    if (verifying) {
        return (
            <div className="flex items-center justify-center min-h-screen w-full bg-black" style={backgroundStyle}>
                <div className="bg-black/90 border border-white/20 p-8 rounded-lg flex flex-col items-center">
                    <div className="w-12 h-12 border-4 border-amber-400 border-t-transparent rounded-full animate-spin"></div>
                    <p className="mt-4 text-lg font-semibold text-white">Verifying reset link...</p>
                </div>
            </div>
        );
    }

    if (!tokenValid) {
        return (
            <div className="flex items-center justify-center min-h-screen w-full bg-black" style={backgroundStyle}>
                <div className="w-full max-w-md px-8 py-10 mx-4 rounded-lg border border-red-500/20 bg-black/80 backdrop-blur-sm">
                    <div className="flex justify-center mb-8">
                        <XCircle className="w-16 h-16 text-red-400" />
                    </div>
                    <h2 className="text-2xl font-bold text-white text-center mb-4">Invalid Reset Link</h2>
                    <p className="text-white/70 text-center mb-6">
                        This password reset link is invalid or has expired. Please request a new one.
                    </p>
                    <button
                        onClick={() => window.location.href = "/login"}
                        className="w-full bg-white hover:bg-white/90 text-black font-medium py-3 rounded-full transition-colors"
                    >
                        Back to Login
                    </button>
                </div>
            </div>
        );
    }

    if (success) {
        return (
            <div className="flex items-center justify-center min-h-screen w-full bg-black" style={backgroundStyle}>
                <div className="w-full max-w-md px-8 py-10 mx-4 rounded-lg border border-green-500/20 bg-black/80 backdrop-blur-sm">
                    <div className="flex justify-center mb-8">
                        <CheckCircle className="w-16 h-16 text-green-400" />
                    </div>
                    <h2 className="text-2xl font-bold text-white text-center mb-4">Password Reset Successful!</h2>
                    <p className="text-white/70 text-center mb-6">
                        Your password has been successfully reset. You can now log in with your new password.
                    </p>
                    <p className="text-white/50 text-center text-sm">
                        Redirecting to login page in 3 seconds...
                    </p>
                </div>
            </div>
        );
    }

    return (
        <div className="flex items-center justify-center min-h-screen w-full bg-black" style={backgroundStyle}>
            {loading && (
                <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50">
                    <div className="bg-black/90 border border-white/20 p-5 rounded-lg flex flex-col items-center">
                        <div className="w-12 h-12 border-4 border-amber-400 border-t-transparent rounded-full animate-spin"></div>
                        <p className="mt-4 text-lg font-semibold text-white">Resetting password...</p>
                    </div>
                </div>
            )}

            <div className="w-full max-w-md px-8 py-10 mx-4 rounded-lg border border-white/20 bg-black/80 backdrop-blur-sm">
                <div className="flex justify-center mb-8">
                    <div className="text-center">
                        <div className="w-24 h-24 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center">
                            <Lock className="w-12 h-12 text-white" />
                        </div>
                    </div>
                </div>

                <h2 className="text-2xl font-bold text-white text-center mb-2">Reset Password</h2>
                {userEmail && (
                    <p className="text-white/70 text-center mb-6 text-sm">
                        Resetting password for: {userEmail}
                    </p>
                )}

                <div className="space-y-6">
                    <div className="relative">
                        <input
                            id="newPassword"
                            name="newPassword"
                            type={showNewPassword ? "text" : "password"}
                            required
                            placeholder="New Password"
                            value={newPassword}
                            onChange={(e) => setNewPassword(e.target.value)}
                            className="w-full bg-transparent border-b border-t-0 border-x-0 border-white/30 rounded-none px-8 py-3 text-white placeholder:text-white/70 focus:outline-none focus:border-white transition-colors"
                        />
                        <Lock className="absolute left-0 top-3 h-5 w-5 text-white/50" />
                        <button
                            type="button"
                            className="absolute right-0 top-3 text-white hover:text-white/80 transition-colors"
                            onClick={() => setShowNewPassword(!showNewPassword)}
                        >
                            {showNewPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                        </button>
                    </div>

                    {newPassword && (
                        <div className="space-y-2">
                            <div className="flex items-center justify-between">
                                <span className="text-sm text-white/70">Password Strength:</span>
                                <span className={`text-sm font-medium ${getPasswordStrengthColor()}`}>
                                    {getPasswordStrengthText()}
                                </span>
                            </div>
                            <div className="space-y-1 text-xs">
                                <div className={`flex items-center ${passwordStrength.length ? 'text-green-400' : 'text-white/50'}`}>
                                    <span className="mr-2">{passwordStrength.length ? '✓' : '○'}</span>
                                    At least 8 characters
                                </div>
                                <div className={`flex items-center ${passwordStrength.uppercase ? 'text-green-400' : 'text-white/50'}`}>
                                    <span className="mr-2">{passwordStrength.uppercase ? '✓' : '○'}</span>
                                    One uppercase letter
                                </div>
                                <div className={`flex items-center ${passwordStrength.lowercase ? 'text-green-400' : 'text-white/50'}`}>
                                    <span className="mr-2">{passwordStrength.lowercase ? '✓' : '○'}</span>
                                    One lowercase letter
                                </div>
                                <div className={`flex items-center ${passwordStrength.number ? 'text-green-400' : 'text-white/50'}`}>
                                    <span className="mr-2">{passwordStrength.number ? '✓' : '○'}</span>
                                    One number
                                </div>
                            </div>
                        </div>
                    )}

                    <div className="relative">
                        <input
                            id="confirmPassword"
                            name="confirmPassword"
                            type={showConfirmPassword ? "text" : "password"}
                            required
                            placeholder="Confirm New Password"
                            value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                            className="w-full bg-transparent border-b border-t-0 border-x-0 border-white/30 rounded-none px-8 py-3 text-white placeholder:text-white/70 focus:outline-none focus:border-white transition-colors"
                        />
                        <Lock className="absolute left-0 top-3 h-5 w-5 text-white/50" />
                        <button
                            type="button"
                            className="absolute right-0 top-3 text-white hover:text-white/80 transition-colors"
                            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        >
                            {showConfirmPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                        </button>
                    </div>

                    {confirmPassword && newPassword !== confirmPassword && (
                        <p className="text-red-400 text-xs">Passwords do not match</p>
                    )}

                    <button
                        type="button"
                        onClick={handleSubmit}
                        disabled={loading || !newPassword || !confirmPassword || newPassword !== confirmPassword}
                        className="w-full bg-white hover:bg-white/90 text-black font-medium py-3 rounded-full disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                    >
                        {loading ? "Resetting..." : "Reset Password"}
                    </button>
                </div>

                <div className="mt-6 text-center">
                    <button
                        onClick={() => window.location.href = "/login"}
                        className="text-sm text-white/70 hover:text-white transition-colors"
                    >
                        Back to Login
                    </button>
                </div>
            </div>
        </div>
    );
}